/**
 * 現場の図（元仕様 §3.9 の土量・土工計画・工程のタブ）。
 * サンプル WebUI-Sample/3D-Portal-Google3D.html の ganttSVG（S948-963）・scurveSVG（S966-983）・
 * massSVG（S885-899）・drawHeat（S848-859）と同じ寸法・文字の大きさ・色・凡例で描く。
 * どれも SVG の文字列を返す。数は計算部品の結果だけから描き、文字はエスケープする。
 * 色はサンプルと同じ値を属性で持つ（スタイルシートがなくても同じ見た目になる）。class は試験と画面の目印。
 */
import { clamp } from '../core/math';
import { pngDataUrl } from '../core/png';
import { escapeHtml as esc, formatFixed1 as f1, formatInt as fi, safeColor } from '../core/text';
import { DAY_MS, formatDay } from '../core/time';
import type { DayNumber } from '../core/time';
import { B, T_MAX, T_MIN, stateAt, taskProgress } from '../model';
import type { SiteView } from '../app/store';
import { monthTicks, ym } from './charts';

const n1 = (v: number) => v.toFixed(1);
const MUTED = '#93a1bd';

/** ガントと出来高曲線で共通の横軸（サンプルの ganttGeom）。 */
export interface GanttGeom {
  t0: DayNumber;
  t1: DayNumber;
  W: number;
  LW: number;
}
export function ganttGeom(v: SiteView): GanttGeom {
  const s = v.site;
  return {
    t0: s.planStart - 20,
    // 予測が表示期間より先になる極端な計画でも、横軸は表示期間の終わり（＋余白）で打ち切る。
    // 打ち切らないと数十万日ぶんの目盛りと点を作ってしまう。期間外の予測は帯が右端に張りつく
    t1: Math.min(Math.max(s.planEnd, v.cur.end, v.base.end), T_MAX) + 25,
    W: 900,
    LW: 170,
  };
}

/** ガント上の x（viewBox の座標）から日付を求める。名前の列の上なら null（FR-SCHED-04）。 */
export function dayAtGanttX(g: GanttGeom, vx: number): DayNumber | null {
  if (vx < g.LW) return null;
  return g.t0 + ((vx - g.LW) / (g.W - g.LW - 12)) * (g.t1 - g.t0);
}

/**
 * 現場のガントチャート（サンプルの ganttSVG）。灰＝計画、色＝予測、濃い色＝基準日の実績。
 * クリックと左右キーで表示日を動かす（portal が data-* を読む）。
 */
export function siteGantt(v: SiteView, T: DayNumber): string {
  const s = v.site;
  const g = ganttGeom(v);
  const RH = 28;
  const Tp = 28;
  const H = Tp + s.tasks.length * RH + 8;
  const X = (d: number) =>
    g.LW + ((clamp(d, g.t0, g.t1) - g.t0) / (g.t1 - g.t0)) * (g.W - g.LW - 12);
  const months = (g.t1 - g.t0) / 30;
  const every = months > 26 ? 3 : months > 14 ? 2 : 1;
  const col = safeColor(s.color);
  let h = `<svg class="chart gantt" id="gantt" viewBox="0 0 ${g.W} ${H}" style="cursor:crosshair" role="slider" tabindex="0" aria-label="ガントチャート（灰＝計画、色＝予測）。クリックか左右キー（Shift で 30 日）で表示日を移動" aria-valuemin="${T_MIN}" aria-valuemax="${T_MAX}" aria-valuenow="${Math.floor(clamp(T, T_MIN, T_MAX))}" aria-valuetext="${formatDay(T)}" data-t0="${g.t0}" data-t1="${g.t1}" data-w="${g.W}" data-lw="${g.LW}">`;
  h += monthTicks(g.t0, g.t1, X, Tp - 6, H - 4, every, s.region.ws ? s.region : null);
  s.tasks.forEach((t, i) => {
    const y = Tp + i * RH;
    const f = v.cur.fc[i]!;
    const late = f.F - t.te;
    const name = t.name.length > 10 ? `${t.name.slice(0, 10)}…` : t.name;
    h += `<text x="6" y="${y + 17}" font-size="13" fill="#e2e8f0" class="rowlabel">${esc(name)}</text>`;
    h += `<rect x="${n1(X(t.ts))}" y="${y + 3}" width="${n1(Math.max(1, X(t.te) - X(t.ts)))}" height="5" rx="2" fill="#475569" class="planbar"/>`;
    const fx = X(f.S);
    const fw = Math.max(2, X(f.F) - fx);
    h += `<rect x="${n1(fx)}" y="${y + 10}" width="${n1(fw)}" height="13" rx="3" fill="${col}" fill-opacity=".28" stroke="${col}" stroke-opacity=".8"/>`;
    if (t.a > 0)
      h += `<rect x="${n1(fx)}" y="${y + 10}" width="${n1(fw * t.a)}" height="13" rx="3" fill="${col}"/>`;
    const cls = late > 14 ? 'late' : late > 0 ? 'warn' : 'muted';
    const fill = late > 14 ? '#fca5a5' : late > 0 ? '#fde68a' : MUTED;
    const lateText = late > 0 ? `  +${late}日` : late < 0 ? `  ${late}日` : '';
    // 表示期間の先の予測は、帯が右端に張りつくので言葉で示す（サンプルにはない）
    const out = f.F > T_MAX ? '（表示期間外）' : '';
    h += `<text x="${n1(Math.min(g.W - 4, fx + fw + 5))}" y="${y + 21}" font-size="12" fill="${fill}" class="barlabel ${cls}">${Math.round(t.a * 100)}%${lateText}${out}${t.earth ? '  ◆モデル予測' : ''}</text>`;
  });
  const xb = n1(X(B));
  const xt = n1(X(clamp(T, T_MIN, T_MAX)));
  h += `<line x1="${xb}" x2="${xb}" y1="${Tp - 4}" y2="${H - 4}" stroke="#fff" stroke-dasharray="4 3" class="baseline"/><text x="${n1(X(B) + 3)}" y="${H - 6}" font-size="9.5" fill="#fff" class="ticklabel">基準日</text>`;
  h += `<line x1="${xt}" x2="${xt}" y1="${Tp - 4}" y2="${H - 4}" stroke="#f59e0b" stroke-width="2" class="nowline"/><text x="${n1(Number(xt) + 3)}" y="${Tp + 7}" font-size="9.5" fill="#fde68a" class="nowlabel">${formatDay(T)}</text>`;
  if (s.region.ws)
    h += `<text x="${g.W - 8}" y="${H - 6}" font-size="9.5" fill="#93c5fd" text-anchor="end" class="ticklabel winterlabel">青帯＝冬期</text>`;
  return `${h}</svg>`;
}

/** 遅延の連鎖。竣工を決めた工種から先行工種をさかのぼる（最大 8 件）。 */
export function delayChain(v: SiteView): string {
  const s = v.site;
  let i = v.cur.fc.findIndex((f) => f.F === v.cur.end);
  const chain: string[] = [];
  while (i >= 0 && chain.length < 8) {
    const t = s.tasks[i]!;
    const late = v.cur.fc[i]!.F - t.te;
    chain.unshift(`${t.name}${late > 0 ? `(+${late})` : ''}`);
    i = t.pred;
  }
  return chain.join(' → ');
}

/**
 * 出来高曲線（S カーブ。サンプルの scurveSVG）。計画・実績・予測を描き、
 * What-if のときは初期計画の予測も基準日から先だけ描く。
 */
export function sCurve(v: SiteView, T: DayNumber, whatIf: boolean): string {
  const s = v.site;
  const { t0, t1 } = ganttGeom(v);
  const W = 420;
  const H = 230;
  const L = 36;
  const Rm = 10;
  const Tp = 14;
  const Bm = 24;
  const X = (d: number) => L + ((d - t0) / (t1 - t0)) * (W - L - Rm);
  const Y = (p: number) => Tp + (1 - p) * (H - Tp - Bm);
  const pl: string[] = [];
  const ac: string[] = [];
  const pr: string[] = [];
  const pb: string[] = [];
  for (let d = t0; d <= t1; d += 5) {
    const st = stateAt(s, d, v.cur);
    pl.push(`${n1(X(d))},${n1(Y(st.plan))}`);
    if (d <= B) ac.push(`${n1(X(d))},${n1(Y(st.overall))}`);
    if (d >= B - 5) pr.push(`${n1(X(d))},${n1(Y(st.overall))}`);
    if (whatIf && d >= B) pb.push(`${n1(X(d))},${n1(Y(stateAt(s, d, v.base).overall))}`);
  }
  const col = safeColor(s.color);
  let h = `<svg class="chart scurve" viewBox="0 0 ${W} ${H}" role="img" aria-label="出来高曲線（計画・実績・予測）">`;
  for (let k = 0; k <= 4; k++) {
    const y = n1(Y(k / 4));
    h += `<line x1="${L}" x2="${W - Rm}" y1="${y}" y2="${y}" stroke="#22304d" class="tick"/><text x="${L - 4}" y="${n1(Y(k / 4) + 3)}" font-size="9.5" fill="${MUTED}" text-anchor="end" class="ticklabel">${k * 25}%</text>`;
  }
  h += monthTicks(t0, t1, X, Tp, H - Bm, 99, s.region.ws ? s.region : null);
  for (let d = Math.ceil(t0); d <= t1; d++) {
    const dt = new Date(d * DAY_MS);
    if (dt.getUTCDate() === 1 && dt.getUTCMonth() % 6 === 0)
      h += `<text x="${n1(X(d))}" y="${H - 8}" font-size="9.5" fill="${MUTED}" text-anchor="middle" class="ticklabel">${ym(d)}</text>`;
  }
  h += `<polyline points="${pl.join(' ')}" fill="none" stroke="#94a3b8" stroke-width="1.6" stroke-dasharray="5 4" class="line-plan"/>`;
  if (pb.length)
    h += `<polyline points="${pb.join(' ')}" fill="none" stroke="#64748b" stroke-width="1.4" class="line-base"/>`;
  h += `<polyline points="${pr.join(' ')}" fill="none" stroke="#f59e0b" stroke-width="2" stroke-dasharray="6 3" class="line-pred"/><polyline points="${ac.join(' ')}" fill="none" stroke="${col}" stroke-width="2.4" class="line-actual"/>`;
  const Tc = clamp(T, t0, t1);
  const now = stateAt(s, Tc, v.cur).overall;
  h += `<line x1="${n1(X(B))}" x2="${n1(X(B))}" y1="${Tp}" y2="${H - Bm}" stroke="#fff" stroke-dasharray="4 3" opacity=".7" class="baseline"/><line x1="${n1(X(Tc))}" x2="${n1(X(Tc))}" y1="${Tp}" y2="${H - Bm}" stroke="#f59e0b" class="nowline"/><circle cx="${n1(X(Tc))}" cy="${n1(Y(now))}" r="4" fill="#f59e0b" class="nowdot"/>`;
  h += `<text x="${L + 4}" y="${Tp + 10}" font-size="10" fill="#94a3b8" class="legend">┄ 計画</text><text x="${L + 52}" y="${Tp + 10}" font-size="10" fill="${col}" class="legend">━ 実績</text><text x="${L + 100}" y="${Tp + 10}" font-size="10" fill="#fde68a" class="legend legend-pred">┅ 予測</text>${pb.length ? `<text x="${L + 148}" y="${Tp + 10}" font-size="10" fill="#94a3b8" class="legend legend-base">━ 初期計画の予測</text>` : ''}`;
  return `${h}</svg>`;
}

/** 切盛の色（0〜255 の RGB）。v は現況 − 計画面（m、プラスが切土）。±4m で最も濃くし、白から赤・青へ連続に変える。 */
export function cutFillRgb(v: number): [number, number, number] {
  const t = clamp(v / 4, -1, 1);
  const white = [0.97, 0.98, 0.99];
  const to = t > 0 ? [0xdc / 255, 0x26 / 255, 0x26 / 255] : [0x25 / 255, 0x63 / 255, 0xeb / 255];
  const a = Math.abs(t);
  const [r, g, b] = white.map((w, i) => Math.round((w + (to[i]! - w) * a) * 255));
  return [r!, g!, b!];
}

/** 切盛の色（#rrggbb）。 */
export const cutFillColor = (v: number): string =>
  `#${cutFillRgb(v)
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('')}`;

/** 各マスの「現況 − 計画面」（四隅の平均）。行は北から南、列は西から東。 */
export function cutFillCells(v: SiteView): Float64Array {
  const g = v.site.g;
  const V = g.nx + 1;
  const out = new Float64Array(g.nx * g.nz);
  for (let j = 0; j < g.nz; j++) {
    for (let i = 0; i < g.nx; i++) {
      let sum = 0;
      for (const [a, b] of [
        [0, 0],
        [1, 0],
        [0, 1],
        [1, 1],
      ] as const) {
        const k = (j + b) * V + i + a;
        sum += g.E[k]! - g.D[k]!;
      }
      out[j * g.nx + i] = sum / 4;
    }
  }
  return out;
}

/**
 * 切盛分布図（サンプルの drawHeat。上が北）。各マスの値は四隅の「現況 − 計画面」の平均。
 * サンプルは canvas だが、ここではマスを 1 画素にした PNG を拡大して SVG に置く（大きさはサンプルと同じ）。
 */
export function cutFillMap(v: SiteView): string {
  const s = v.site;
  const g = s.g;
  const sc = Math.max(2, Math.floor(320 / g.nx));
  const W = g.nx * sc;
  const H = g.nz * sc;
  // マスごとの rect では数千の要素になり、画面が重くなる
  const cells = cutFillCells(v);
  const rgb = new Uint8Array(cells.length * 3);
  cells.forEach((c, k) => rgb.set(cutFillRgb(c), k * 3));
  const image = `<image href="${pngDataUrl(g.nx, g.nz, rgb)}" x="0" y="0" width="${W}" height="${H}" preserveAspectRatio="none" style="image-rendering:pixelated" class="heatimg"/>`;
  const px = (x: number) => ((x + s.area.w / 2) / s.area.w) * W;
  const pz = (z: number) => ((z + s.area.d / 2) / s.area.d) * H;
  let blocks = '';
  for (const b of s.blocks) {
    // 道路は x0〜x1 の帯（幅 w）、ほかは中心 (x, z) と幅・奥行き
    const r =
      b.kind === 'road'
        ? { x: Math.min(b.x0, b.x1), z: b.z - b.w / 2, w: Math.abs(b.x1 - b.x0), d: b.w }
        : { x: b.x - b.w / 2, z: b.z - b.d / 2, w: b.w, d: b.d };
    blocks += `<rect x="${n1(px(r.x))}" y="${n1(pz(r.z))}" width="${n1((r.w / s.area.w) * W)}" height="${n1((r.d / s.area.d) * H)}" fill="none" stroke="rgba(15,23,42,.9)" stroke-width="2" class="heatblock"/>`;
  }
  // ゲートは実際の位置（z）に、下向きの三角で描く
  const gx = px(s.gate.x);
  const gy = pz(s.gate.z) - 6;
  return `<svg class="chart heat" viewBox="0 0 ${W} ${H}" role="img" aria-label="切盛分布図（赤＝切土、青＝盛土、上が北）">${image}${blocks}<polygon points="${n1(gx)},${n1(gy + 8)} ${n1(gx - 7)},${n1(gy - 5)} ${n1(gx + 7)},${n1(gy - 5)}" fill="#f59e0b" class="gate"/><text x="6" y="16" font-size="13" font-weight="bold" font-family="sans-serif" fill="#0f172a" class="north">N↑</text></svg>`;
}

/** 土積曲線（マスカーブ。サンプルの massSVG）。西から東へ「流用切土 × C − 盛土」を累計し、測点別の切土・盛土を柱で示す。 */
export function massCurve(v: SiteView): string {
  const s = v.site;
  const eb = v.cur.eb;
  const n = eb.raw.length;
  const W = 520;
  const H = 230;
  const L = 48;
  const Rm = 10;
  const Tp = 14;
  const Bm = 28;
  const vals = [0, ...eb.raw];
  const mx = Math.max(...vals);
  const mn = Math.min(...vals);
  const span = mx - mn || 1;
  const X = (i: number) => L + ((i + 1) / n) * (W - L - Rm);
  const Y = (m: number) => Tp + ((mx - m) / span) * (H - Tp - Bm);
  const bmax = Math.max(...eb.sup, ...eb.dem, 1);
  const y0 = Y(0);
  let ticks = '';
  for (let k = 0; k <= 4; k++) {
    const m = mn + (span * k) / 4;
    ticks += `<line x1="${L}" x2="${W - Rm}" y1="${n1(Y(m))}" y2="${n1(Y(m))}" stroke="#22304d" class="tick"/><text x="${L - 4}" y="${n1(Y(m) + 3)}" font-size="9.5" fill="${MUTED}" text-anchor="end" class="ticklabel">${f1(m / 1000)}k</text>`;
  }
  let bars = '';
  const step = (W - L - Rm) / n;
  const bw = Math.max(1, step * 0.7);
  for (let i = 0; i < n; i++) {
    const x = X(i) - step * 0.5;
    const hs = (eb.sup[i]! / bmax) * 40;
    const hd = (eb.dem[i]! / bmax) * 40;
    bars += `<rect x="${n1(x - bw / 2)}" y="${n1(y0 - hs)}" width="${n1(bw)}" height="${n1(hs)}" fill="#dc2626" opacity=".55" class="col-cut"/><rect x="${n1(x - bw / 2)}" y="${n1(y0)}" width="${n1(bw)}" height="${n1(hd)}" fill="#2563eb" opacity=".55" class="col-fill"/>`;
  }
  const pts = eb.raw.map((m, i) => `${n1(X(i))},${n1(Y(m))}`).join(' ');
  let xl = '';
  for (let k = 0; k <= 4; k++) {
    const i = Math.round(((n - 1) * k) / 4);
    xl += `<text x="${n1(X(i))}" y="${H - 12}" font-size="9.5" fill="${MUTED}" text-anchor="middle" class="ticklabel">${Math.round(-s.area.w / 2 + (i + 1) * s.g.dx)}m</text>`;
  }
  const gx = n1(X(eb.gi));
  return `<svg class="chart mass" viewBox="0 0 ${W} ${H}" role="img" aria-label="土積曲線（累加土量）と測点別の切土・盛土">${ticks}<line x1="${L}" x2="${W - Rm}" y1="${n1(y0)}" y2="${n1(y0)}" stroke="#64748b" class="zero"/>${bars}<polyline points="${L},${n1(y0)} ${pts}" fill="none" stroke="#f59e0b" stroke-width="2" class="line-mass"/><line x1="${gx}" x2="${gx}" y1="${Tp}" y2="${H - Bm}" stroke="#fde68a" stroke-dasharray="3 3" class="gateline"/><text x="${n1(Number(gx) + 3)}" y="${Tp + 9}" font-size="10" fill="#fde68a" class="ticklabel gatelabel">ゲート</text>${xl}<text x="${W - Rm}" y="${H - 1}" font-size="9.5" fill="${MUTED}" text-anchor="end" class="ticklabel">測点（西→東）</text></svg>`;
}

/** 土積曲線の注記（サンプルの massNote の中身。囲みの div は呼ぶ側でつける）。 */
export function massNote(v: SiteView): string {
  const eb = v.cur.eb;
  const end = eb.raw[eb.raw.length - 1] ?? 0;
  return `曲線：累加土量（流用切土×C − 盛土）。柱：測点別 切土（赤）・盛土（青）。終点（${end >= 0 ? '+' : ''}${fi(end)} m³）が余剰（＋）／不足（−）。ゲートで残土・購入土を受け払いした場合の場内平均運搬距離は <b>${fi(eb.avgHaul)} m</b>。`;
}

/** 運搬サイクルの内訳（分）。元仕様 §6.6 の式の項（積込・往復・荷下ろし）に分ける。 */
export function haulCycles(
  v: SiteView,
): { name: string; parts: [string, number][]; total: number }[] {
  const { cap } = v.cur;
  const intTrip = ((2 * cap.dInt) / 1000 / 15) * 60;
  const extTrip = ((2 * v.plan.dExt) / 30) * 60;
  return [
    {
      name: `場内（平均 ${fi(cap.dInt)} m）`,
      parts: [
        ['積込', 4],
        ['往復', intTrip],
        ['荷下ろし', 2],
      ],
      total: cap.cycInt,
    },
    {
      name: `場外（${fi(v.plan.dExt)} km）`,
      parts: [
        ['積込', 5],
        ['往復', extTrip],
        ['荷下ろし', 3],
      ],
      total: cap.cycExt,
    },
  ];
}

/** 運搬サイクルの横棒（積込・往復・荷下ろしの積み上げ）。 */
export function haulCycleChart(v: SiteView): string {
  const rows = haulCycles(v);
  const W = 520;
  const L = 130;
  const RH = 26;
  const H = rows.length * RH + 8;
  const max = Math.max(...rows.map((r) => r.total));
  const X = (m: number) => (m / max) * (W - L - 70);
  let h = `<svg class="chart cycle" viewBox="0 0 ${W} ${H}" role="img" aria-label="運搬サイクルの内訳（分）">`;
  rows.forEach((r, i) => {
    const y = 4 + i * RH;
    let x = L;
    h += `<text x="4" y="${y + 15}" class="rowlabel">${esc(r.name)}</text>`;
    r.parts.forEach(([name, m], k) => {
      const w = X(m);
      h += `<rect x="${n1(x)}" y="${y + 3}" width="${n1(Math.max(0.5, w))}" height="16" class="cyc${k}"><title>${esc(name)} ${m.toFixed(1)} 分</title></rect>`;
      x += w;
    });
    h += `<text x="${n1(x + 6)}" y="${y + 15}" class="barlabel">${r.total.toFixed(1)} 分</text>`;
  });
  return `${h}</svg>`;
}

/** 施工手順（サンプルの renderConstTab の steps。表示日の時点の予測工程）。 */
export function constructionSteps(v: SiteView, T: DayNumber): string {
  const s = v.site;
  return `<ul class="steps">${s.tasks
    .map((t, i) => {
      const p = taskProgress(s, i, T, v.cur);
      const f = v.cur.fc[i]!;
      const cls = p >= 1 ? 'done' : p > 0 ? 'cur' : '';
      return `<li class="${cls}"><span class="no">${p >= 1 ? '✓' : i + 1}</span><div><b>${esc(t.name)}</b> <span class="meta">${formatDay(f.S)}〜${formatDay(f.F)}（${Math.round(p * 100)}%）</span><div class="meta">${esc(t.method)}</div></div></li>`;
    })
    .join('')}</ul>`;
}
